import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEligibilityData } from "../useEligibilityData";
import axios from "axios";
import Navbar from "../../../components/molecules/Navbar";
import Footer from "../../../components/molecules/Footer";
import styles from "../index.module.css";
import emiStyles from "./index.module.css";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const getEmiDates = () => {
  const today = new Date();
  const todayDay = today.getDate();
  const startDay = todayDay >= 21 ? 10 : 5;
  const baseOptions = [5, 10, 15, 20];
  const startIndex = baseOptions.indexOf(startDay);

  return Array.from({ length: 3 }, (_, i) => {
    const optIdx = (startIndex + i) % 3;
    const wrapped = optIdx < startIndex;
    const monthOffset = wrapped ? 2 : 1;
    const day = baseOptions[optIdx];
    const date = new Date(today.getFullYear(), today.getMonth() + monthOffset, day);
    const suffix = day === 1 || day === 21 || day === 31 ? "st" : day === 2 || day === 22 ? "nd" : day === 3 || day === 23 ? "rd" : "th";
    return {
      value: day,
      display: `${day}${suffix}`,
      month: MONTHS[date.getMonth()],
      year: date.getFullYear(),
      date,
    };
  });
};

const DATE_OPTIONS = getEmiDates();

function EligibilityEmiDate() {
  const navigate = useNavigate();
  const userData = useEligibilityData();

  const [selectedDate, setSelectedDate] = useState(DATE_OPTIONS[0].value);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleConfirm = async () => {
    const selected = DATE_OPTIONS.find(o => o.value === selectedDate);
    const emiFirstDate = selected
      ? `${selected.date.getFullYear()}-${String(selected.date.getMonth() + 1).padStart(2, "0")}-${String(selected.date.getDate()).padStart(2, "0")}`
      : "";

    const token = sessionStorage.getItem("eligibility_auth_token");
    const appId = userData?.applicationId;
    if (!appId || !token) {
      navigate("/eligibility/evaluating", { state: { data: userData, emiDate: selectedDate, emiFirstDate } });
      return;
    }

    setLoading(true);
    setError("");
    try {
      const urlData = JSON.parse(sessionStorage.getItem("eligibility_url_data") || "{}");
      let loanAmount = urlData.courseFees || 0;
      const loanTenure = urlData.loanTenure;

      let productId = urlData.productId || urlData.producId || urlData.product_id || "";

      // Fetch summary once if loanAmount or instituteId needs to come from it
      let summaryCache: any = null;
      const fetchSummary = async () => {
        if (summaryCache) return summaryCache;
        const res = await fetch(
          `${process.env.REACT_APP_DASHBOARD_URL}/summary/user?id=${userData?.userId}&applicationId=${appId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        summaryCache = (await res.json())?.data || {};
        return summaryCache;
      };

      if (!loanAmount) {
        const summary = await fetchSummary();
        loanAmount = summary?.loanAmount || summary?.courseFees || 0;
      }

      if (!productId) {
        const instituteName = urlData.instituteName || "";
        let instituteId = "";

        if (instituteName) {
          const instRes = await fetch(
            `${process.env.REACT_APP_DASHBOARD_URL}/institute/info/name?name=${encodeURIComponent(instituteName)}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const instData = await instRes.json();
          instituteId = instData?.data?.[0]?.id || "";
        } else {
          const summary = await fetchSummary();
          instituteId = summary?.instituteId || "";
        }

        const productsRes = await fetch(
          `${process.env.REACT_APP_DASHBOARD_URL}/products/instituteId/active?instituteId=${instituteId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const productsData = await productsRes.json();
        const products: any[] = productsData?.data || [];
        const matched = products.filter((p) => p.tenure === loanTenure);
        productId = (matched[0] || products[0])?.productId || "";
      }

      const emiRes = await fetch(
        `${process.env.REACT_APP_DASHBOARD_URL}/admin/application/emi?applicationId=${appId}&productId=${productId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const emiData = await emiRes.json();
      const emi = emiData.data || 4000;

      await axios.post(
        `${process.env.REACT_APP_DASHBOARD_URL}/admin/application/assign-fund-source`,
        { applicationId: appId, fundCode: "GBP", productId, loanAmount, emi, emiFirstDate, dayOfEmi: selectedDate, rulesStatus: 1 },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      navigate("/eligibility/evaluating", { state: { data: userData, emiDate: selectedDate, emiFirstDate } });
    } catch (err) {
      console.log("assign-fund-source error:", err);
      setError("Oops! There is an issue at our end. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.body}>
        <div className={styles.backdrop}>
          <Navbar />
          <div className={styles.main}>
            <div className={styles.cardWrapper}>
              <div className={styles.container} style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "260px", gap: "1rem" }}>
                <svg width="40" height="40" viewBox="0 0 34 34" fill="none" style={{ animation: "spin 1s linear infinite" }}>
                  <circle cx="17" cy="17" r="13" stroke="rgba(211,32,40,0.2)" strokeWidth="3" />
                  <path d="M17 4C24.18 4 30 9.82 30 17" stroke="#d32028" strokeWidth="3" strokeLinecap="round" />
                </svg>
                <p style={{ margin: 0, fontSize: "0.9rem", color: "#555", fontFamily: "Outfit, sans-serif" }}>Processing your selection...</p>
              </div>
              <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
              <Footer light />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.body}>
      <div className={styles.backdrop}>
        <Navbar />
        <div className={styles.main}>
          <div className={styles.cardWrapper}>
            <div className={styles.container}>

              {/* ── Header ── */}
              <div className={styles.cardHeader}>
                <div style={{
                  width: "2.4rem", height: "2.4rem", borderRadius: "50%",
                  background: "#F4E5DD", display: "flex", alignItems: "center",
                  justifyContent: "center", flexShrink: 0,
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="4" width="18" height="18" rx="2" fill="#d32028" opacity="0.12" stroke="#d32028" strokeWidth="1.5" />
                    <line x1="3" y1="9" x2="21" y2="9" stroke="#d32028" strokeWidth="1.5" />
                    <line x1="8" y1="2" x2="8" y2="6" stroke="#d32028" strokeWidth="1.5" strokeLinecap="round" />
                    <line x1="16" y1="2" x2="16" y2="6" stroke="#d32028" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </div>
                <p className={styles.cardTitle}>Select EMI Start Date</p>
              </div>
{/* 
              <p style={{ fontSize: "0.78rem", color: "#777", margin: "0 0 0.75rem", lineHeight: 1.5 }}>
                Your first EMI will be deducted on this date.
              </p> */}

              {/* ── Date grid ── */}
              <div className={emiStyles.grid}>
                {DATE_OPTIONS.map((opt) => {
                  const isSelected = selectedDate === opt.value;
                  return (
                    <div
                      key={opt.value}
                      className={`${emiStyles.tile} ${isSelected ? emiStyles.tileSelected : ""}`}
                      onClick={() => setSelectedDate(opt.value)}
                    >
                      {isSelected && (
                        <div className={emiStyles.tileCheck}>
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                            <path d="M5 13L9 17L19 7" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>
                      )}
                      <div className={`${emiStyles.tileIconBox} ${isSelected ? emiStyles.tileIconBoxSelected : ""}`}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                          <rect x="3" y="4" width="18" height="18" rx="2" stroke={isSelected ? "white" : "#d32028"} strokeWidth="1.6" />
                          <line x1="3" y1="9" x2="21" y2="9" stroke={isSelected ? "white" : "#d32028"} strokeWidth="1.6" />
                          <line x1="8" y1="2" x2="8" y2="6" stroke={isSelected ? "white" : "#d32028"} strokeWidth="1.6" strokeLinecap="round" />
                          <line x1="16" y1="2" x2="16" y2="6" stroke={isSelected ? "white" : "#d32028"} strokeWidth="1.6" strokeLinecap="round" />
                        </svg>
                      </div>
                      <p className={`${emiStyles.tileDate} ${isSelected ? emiStyles.tileDateSelected : ""}`}>
                        {opt.display} {opt.month}
                      </p>
                      {/* <p className={emiStyles.tileLabel}>{opt.month}</p> */}
                    </div>
                  );
                })}
              </div>

              {/* ── Dynamic confirmation pill ── */}
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "0.55rem",
                background: "#fdecea",
                border: "1px solid #f9d8d6",
                borderRadius: "10px",
                padding: "0.7rem 0.9rem",
                margin: "0.75rem 0 0",
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                  <rect x="3" y="4" width="18" height="18" rx="2" fill="#d32028" opacity="0.12" stroke="#d32028" strokeWidth="1.5" />
                  <line x1="3" y1="9" x2="21" y2="9" stroke="#d32028" strokeWidth="1.5" />
                  <line x1="8" y1="2" x2="8" y2="6" stroke="#d32028" strokeWidth="1.5" strokeLinecap="round" />
                  <line x1="16" y1="2" x2="16" y2="6" stroke="#d32028" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                <p style={{ margin: 0, fontSize: "0.82rem", color: "#b91c1c", lineHeight: 1.5 }}>
                  Your EMIs will be deducted on the{" "}
                  <strong>{DATE_OPTIONS.find(o => o.value === selectedDate)?.display}</strong>{" "}
                  of every month, starting{" "}
                  <strong>{DATE_OPTIONS.find(o => o.value === selectedDate)?.month} {DATE_OPTIONS.find(o => o.value === selectedDate)?.year}</strong>
                </p>
              </div>

              {/* ── Tip box ── */}
              <div className={emiStyles.tipBox}>
                <div className={emiStyles.tipIconBox}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="9" stroke="#d32028" strokeWidth="1.6" />
                    <path d="M12 11V16" stroke="#d32028" strokeWidth="1.8" strokeLinecap="round" />
                    <circle cx="12" cy="8" r="0.8" fill="#d32028" />
                  </svg>
                </div>
                <div>
                  {/* <p className={emiStyles.tipTitle}>Financial Best Practice</p> */}
                  <p className={emiStyles.tipText}>
                    Choose a date 1-2 days after your salary credit. EMI auto-debit on this date each month.
                  </p>
                </div>
              </div>

            </div>

            {error && (
              <p style={{ color: "#d32028", fontSize: "0.82rem", textAlign: "center", margin: "0.5rem 1.25rem 0" }}>{error}</p>
            )}

            {/* ── CTA ── */}
            <div className={emiStyles.ctaWrapper}>
              <button className={emiStyles.ctaBtn} onClick={handleConfirm}>
                Proceed
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12H19M13 6L19 12L13 18" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              {process.env.REACT_APP_CURRENT !== 'prod' && (
                <div style={{ display: "flex", justifyContent: "center", paddingBottom: "0.75rem" }}>
                  <button
                    onClick={() => navigate("/eligibility/under-review", { state: { data: userData } })}
                    style={{ background: "none", border: "none", color: "rgba(255,255,255,0.25)", fontSize: "0.75rem", cursor: "pointer", fontFamily: "Outfit, sans-serif", textDecoration: "underline" }}
                  >
                    Skip
                  </button>
                </div>
              )}
            </div>

           

            <Footer light />
          </div>
        </div>
      </div>
    </div>
  );
}

export default EligibilityEmiDate;
